<template>
  <div class="flex flex-col h-full border pt-2 rounded-md mb-3 border-gray-200 pl-4">
    <h3 
      v-if="!requiresAuth || isAuthenticated"
      class="text-xl font-semibold mb-4">Комментарии ({{ totalCount }})</h3>

    <!-- Form for adding comment - not in scroll -->
    <div v-if="isAuthenticated" class="mb-6 pb-4 border-b border-gray-200">
      <div class="space-y-3">
        <textarea
          v-model="newComment"
          placeholder="Напишите комментарий (минимум 5 символов)..."
          class="w-full p-5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows="2"
        ></textarea>
        <div class="flex justify-end gap-2 pr-2">
          <button
            @click="newComment = ''"
            class="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
          >
            Отменить
          </button>
          <button
            @click="submitComment"
            :disabled="!canSubmitComment"
            class="px-4 py-2 bg-blue-600 text-white text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:bg-blue-700"
          >
            Отправить
          </button>
        </div>
        <p v-if="newComment && newComment.length < 5" class="text-xs text-red-500">
          Минимум {{ 5 - newComment.length }} символов
        </p>
      </div>
    </div>

    <div v-else class="mb-6 pb-4 border-b border-gray-200 text-center text-gray-500 text-sm">
      <p v-if="requiresAuth">Только авторизованные пользователи могут просматривать и оставлять комментарии.<br>
      Пожалуйста, <router-link to="/auth/login" class="text-blue hover:underline">зарегистрируйтесь или авторизуйтесь</router-link>.</p>
      <p v-else><router-link to="/auth/login" class="text-blue hover:underline">Авторизуйтесь</router-link> чтобы добавлять комментарии</p>
    </div>

    <!-- Scrollable comments list - hidden only if requiresAuth and not authenticated -->
    <div v-if="!requiresAuth || isAuthenticated" class="flex-1 overflow-y-auto">
      <div v-if="loading" class="text-center text-gray-500">Загрузка комментариев...</div>

      <div v-else-if="comments.length > 0" class="space-y-4">
        <div v-for="comment in comments" :key="comment.id" class="comment-item">
          <div class="bg-gray-50 rounded-md p-4 mb-3">
            <div class="flex justify-between items-start gap-2 mb-2">
              <div>
                <p class="font-semibold text-sm">{{ comment.author_name }}</p>
                <p class="text-xs text-gray-500">{{ formatDate(comment.created_at) }}</p>
                <p v-if="comment.edited_at" class="text-xs text-orange-500">отредактировано: {{ formatDate(comment.edited_at) }}</p>
              </div>
              <div v-if="canManageComment(comment)" class="flex gap-1">
                <button
                  @click="toggleEditForm(comment.id, comment.comment)"
                  class="text-blue text-xs px-2 py-1 hover:bg-blue-50 rounded"
                  title="Редактировать"
                >
                  ✎
                </button>
                <button
                  @click="deleteComment(comment.id)"
                  class="text-red-500 hover:text-red-700 text-xs px-2 py-1 hover:bg-red-50 rounded"
                  title="Удалить"
                >
                  ✕
                </button>
              </div>
            </div>
            <p v-if="editingId !== comment.id" class="text-sm text-gray-800 whitespace-pre-wrap">{{ comment.comment }}</p>

            <div v-if="editingId === comment.id" class="mt-3 pt-3 border-t border-gray-200">
              <textarea
                v-model="editingText"
                placeholder="Редактируйте комментарий (минимум 5 символов)..."
                class="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="2"
              ></textarea>
              <div class="flex justify-end gap-2 mt-2">
                <button
                  @click="toggleEditForm(comment.id, '')"
                  class="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                >
                  Отменить
                </button>
                <button
                  @click="submitEdit(comment.id)"
                  :disabled="!canSubmitEdit"
                  class="px-3 py-1 bg-blue-600 text-white text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:bg-blue-700"
                >
                  Сохранить
                </button>
              </div>
            </div>

            <button
              v-if="currentUser && editingId !== comment.id"
              @click="toggleReplyForm(comment.id)"
              class="text-blue text-xs mt-2 hover:underline"
            >
              {{ replyingTo === comment.id ? 'Отменить' : 'Ответить' }}
            </button>

            <div v-if="replyingTo === comment.id" class="mt-3 pt-3 border-t border-gray-200">
              <textarea
                v-model="newReply"
                placeholder="Напишите ответ (минимум 5 символов)..."
                class="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="2"
              ></textarea>
              <div class="flex justify-end gap-2 mt-2">
                <button
                  @click="newReply = ''"
                  class="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                >
                  Отменить
                </button>
                <button
                  @click="submitReply(comment.id)"
                  :disabled="!canSubmitReply"
                  class="px-3 py-1 bg-blue-600 text-white text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:bg-blue-700"
                >
                  Отправить ответ
                </button>
              </div>
            </div>
          </div>

          <div v-if="comment.replies && comment.replies.length > 0" class="ml-4 space-y-3">
            <div v-for="reply in comment.replies" :key="reply.id" class="comment-item reply">
              <div class="bg-blue-50 rounded-md p-3">
                <div class="flex justify-between items-start gap-2 mb-2">
                  <div>
                    <p class="font-semibold text-xs">{{ reply.author_name }}</p>
                    <p class="text-xs text-gray-500">{{ formatDate(reply.created_at) }}</p>
                    <p v-if="reply.edited_at" class="text-xs text-orange-500">отредактировано: {{ formatDate(reply.edited_at) }}</p>
                  </div>
                  <div v-if="canManageComment(reply)" class="flex gap-1">
                    <button
                      @click="toggleEditForm(reply.id, reply.comment)"
                      class="text-blue text-xs px-2 py-1 hover:bg-blue-50 rounded"
                      title="Редактировать"
                    >
                      ✎
                    </button>
                    <button
                      @click="deleteComment(reply.id)"
                      class="text-red-500 hover:text-red-700 text-xs px-2 py-1 hover:bg-red-50 rounded"
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <p v-if="editingId !== reply.id" class="text-sm text-gray-800 whitespace-pre-wrap">{{ reply.comment }}</p>

                <div v-if="editingId === reply.id" class="mt-3 pt-3 border-t border-gray-200">
                  <textarea
                    v-model="editingText"
                    placeholder="Редактируйте ответ (минимум 5 символов)..."
                    class="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows="2"
                  ></textarea>
                  <div class="flex justify-end gap-2 mt-2">
                    <button
                      @click="toggleEditForm(reply.id, '')"
                      class="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                    >
                      Отменить
                    </button>
                    <button
                      @click="submitEdit(reply.id)"
                      :disabled="!canSubmitEdit"
                      class="px-3 py-1 bg-blue-600 text-white text-xs rounded disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:bg-blue-700"
                    >
                      Сохранить
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center text-gray-500 py-8">
        <p>Комментариев нет</p>
      </div>
    </div>

    <div v-if="(!requiresAuth || isAuthenticated) && comments.length > 0"
      class="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center pl-2 pr-2 pb-4">
      <button
        @click="previousPage"
        :disabled="currentPage === 1"
        class="px-4 py-2 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:bg-gray-50"
      >
        ← Назад
      </button>
      <p class="text-sm text-gray-600">Страница {{ currentPage }} из {{ totalPages }}</p>
      <button
        @click="nextPage"
        :disabled="currentPage >= totalPages"
        class="px-4 py-2 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:enabled:bg-gray-50"
      >
        Вперед →
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { API_COMMENTS, HEADERS_TOKEN_JSON, DEFAULT_PER_PAGE } from '@/const/uri'
import { mapState } from 'vuex'

export default {
  name: 'CommentsList',
  props: {
    contentType: {
      type: String,
      required: true,
    },
    objectId: {
      type: String,
      required: true,
    },
    requiresAuth: {
      type: Boolean,
      default: false, // Требуется ли авторизация для просмотра комментариев
    },
  },
  data() {
    return {
      comments: [],
      loading: false,
      newComment: '',
      newReply: '',
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      perPage: DEFAULT_PER_PAGE,
      replyingTo: null,
      editingId: null,
      editingText: '',
    }
  },
  computed: {
    ...mapState(['user']),
    currentUser() {
      return this.user || null
    },
    isAuthenticated() {
      return !!localStorage.getItem('user_token') && this.currentUser && Object.keys(this.currentUser).length > 0
    },
    canSubmitComment() {
      return this.newComment.trim().length >= 5
    },
    canSubmitReply() {
      return this.newReply.trim().length >= 5
    },
    canSubmitEdit() {
      return this.editingText.trim().length >= 5
    },
  },
  watch: {
    objectId: {
      handler() {
        this.currentPage = 1
        this.loadComments()
      },
      immediate: true,
    },
  },
  methods: {
    async loadComments() {
      this.loading = true
      try {
        const response = await axios.get(API_COMMENTS.list, {
          params: {
            content_type: this.contentType,
            object_id: this.objectId,
            p: this.currentPage,
            page_size: this.perPage,
          },
          headers: HEADERS_TOKEN_JSON(),
        })

        const data = response.data.data?.data || response.data.data
        this.comments = data.results || data || []
        const count = data.count || data.total_count || 0
        this.totalPages = Math.ceil(count / this.perPage) || 1
        this.totalCount = count || this.comments.length
      } catch (error) {
        console.error('Error loading comments:', error)
      } finally {
        this.loading = false
      }
    },
    async submitComment() {
      if (!this.canSubmitComment) return

      try {
        await axios.post(
          API_COMMENTS.create,
          {
            content_type: this.contentType,
            object_id: this.objectId,
            comment: this.newComment.trim(),
          },
          {
            headers: HEADERS_TOKEN_JSON(),
          }
        )

        this.newComment = ''
        this.loadComments()
      } catch (error) {
        console.error('Error submitting comment:', error)
        alert('Ошибка при добавлении комментария')
      }
    },
    async submitReply(parentCommentId) {
      if (!this.canSubmitReply) return

      try {
        await axios.post(
          API_COMMENTS.create,
          {
            content_type: this.contentType,
            object_id: this.objectId,
            comment: this.newReply.trim(),
            parent_comment: parentCommentId,
          },
          {
            headers: HEADERS_TOKEN_JSON(),
          }
        )

        this.newReply = ''
        this.replyingTo = null
        this.loadComments()
      } catch (error) {
        console.error('Error submitting reply:', error)
        alert('Ошибка при добавлении ответа')
      }
    },
    async deleteComment(commentId) {
      if (!confirm('Вы уверены что хотите удалить этот комментарий?')) return

      try {
        await axios.delete(`${API_COMMENTS.base}${commentId}/`, {
          headers: HEADERS_TOKEN_JSON(),
        })

        this.loadComments()
      } catch (error) {
        console.error('Error deleting comment:', error)
        alert('Ошибка при удалении комментария')
      }
    },
    canManageComment(comment) {
      if (!this.currentUser) return false
      if (this.currentUser.role?.key === 0 || this.currentUser.role?.key === 3) return true
      if (comment.is_self) return true
      return false
    },
    toggleReplyForm(commentId) {
      this.replyingTo = this.replyingTo === commentId ? null : commentId
      this.newReply = ''
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadComments()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.loadComments()
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    toggleEditForm(commentId, currentText) {
      if (this.editingId === commentId) {
        this.editingId = null
        this.editingText = ''
      } else {
        this.editingId = commentId
        this.editingText = currentText
      }
    },
    async submitEdit(commentId) {
      if (!this.canSubmitEdit) return

      try {
        await axios.put(
          `${API_COMMENTS.base}${commentId}/`,
          {
            comment: this.editingText.trim(),
          },
          {
            headers: HEADERS_TOKEN_JSON(),
          }
        )

        this.editingId = null
        this.editingText = ''
        this.loadComments()
      } catch (error) {
        console.error('Error updating comment:', error)
        alert('Ошибка при редактировании комментария')
      }
    },
  },
}
</script>

<style scoped>

</style>
